import React, { useState, useEffect, useMemo } from 'react';
import { API_CONFIG } from '../../config/api';
import type { Insight } from './insightEngine';
import type { YutiRecord } from './yutiTypes';
import { YutiCard } from './YutiCard';
import { buildFestivalSection } from './festivalUtils';
import festivalDescriptions from '../../data/festivalDescriptions.json';

const descriptions = festivalDescriptions as Record<string, string>;

// Pull the "Ayurveda: ..." clause out of a ṛtu-begin festival description.
// v3 narratives don't carry seasonal dietary guidance, so we surface it as
// its own 🍵 paragraph after the planetary sections.
function extractAyurvedaTips(festivals: string[]): string | null {
  if (!festivals || festivals.length === 0) return null;
  const tips: string[] = [];
  for (const raw of festivals) {
    const name = raw.replace(/^\([PA]\):\s*/, '').trim();
    const desc = descriptions[name];
    if (desc && desc.includes('Ayurveda:')) {
      const ayur = desc.split('Ayurveda:')[1]?.trim();
      if (ayur) tips.push(ayur);
    }
  }
  if (tips.length === 0) return null;
  return `🍵 Ayurveda: ${tips.join('; ')}.`;
}

interface InsightsNarrativeProps {
  insights: Insight[];
  date: string; // YYYY-MM-DD
  festivals?: string[]; // Location-aware festivals from panchangaData
}

/**
 * Weave insights into a cohesive 3-4 sentence narrative paragraph,
 * similar to how an astrologer would summarize the day's chart.
 */
const weaveNarrative = (insights: Insight[]): string => {
  if (insights.length === 0) return '';

  const cautions = insights.filter((i) => i.severity === 'caution');
  const auspicious = insights.filter((i) => i.severity === 'auspicious');
  const info = insights.filter((i) => i.severity === 'info');

  const retrogrades = cautions.filter((i) => i.category === 'retrograde');
  const gandanta = cautions.filter((i) => i.category === 'gandanta');
  const conjunctions = insights.filter((i) => i.category === 'conjunction');
  const dignityAuspicious = auspicious.filter((i) => i.category === 'dignity');
  const dignityCaution = cautions.filter((i) => i.category === 'dignity');
  const chandrashtama = cautions.filter((i) => i.category === 'chandrashtama');
  const auspiciousAspects = auspicious.filter((i) => i.category === 'aspect');
  const infoInsights = info.filter((i) => i.category !== 'conjunction');

  const sections: string[] = [];

  // ✨ Section 1: Positives — exaltations, favorable dignities
  if (dignityAuspicious.length > 0) {
    const parts = dignityAuspicious.map((d) => `${d.title} (${d.description}) — ${d.effect.replace(/\.$/, '').toLowerCase()}`);
    sections.push(`✨ ${parts.join(', and ')}.`);
  }

  // ⚡ Section 2: Mixed — conjunctions, interesting formations
  if (conjunctions.length > 0) {
    const parts = conjunctions.map((c) => `${c.description} — ${c.effect.replace(/\.$/, '').toLowerCase()}`);
    sections.push(`⚡ ${parts.join('; ')}.`);
  }

  // ⚠️ Section 3: Cautions grouped — debilitations, retrogrades, gandanta, chandrashtama
  const cautionParts: string[] = [];
  if (dignityCaution.length > 0) {
    cautionParts.push(...dignityCaution.map((d) => `${d.description}, ${d.effect.replace(/\.$/, '').toLowerCase()}`));
  }
  if (retrogrades.length > 0) {
    const names = retrogrades.map((r) => r.description.split(' retrograde')[0]);
    const effects = retrogrades.map((r) => r.effect.replace(/\.$/, '').toLowerCase());
    cautionParts.push(`${names.join(' and ')} in retrograde — ${effects.join(', ')}, a period of review rather than new initiatives`);
  }
  if (gandanta.length > 0) {
    cautionParts.push(...gandanta.map((g) => `${g.title.replace(' in Gandānta', '')} in Gandānta zone — ${g.effect.replace(/\.$/, '').toLowerCase()}`));
  }
  if (chandrashtama.length > 0) {
    const ch = chandrashtama[0];
    cautionParts.push(ch.description.replace('Moon in ', 'the Moon\'s transit through ').replace(' — 8th transit for ', ' brings Chandrāṣṭama for ').replace(' natives', ' natives — postpone major decisions, avoid confrontations, and practice patience'));
  }
  if (cautionParts.length > 0) {
    sections.push(`⚠️ ${cautionParts.join('; ')}.`);
  }

  // 🛡️ Section 4: Silver linings — benefic aspects, supportive formations
  const silverParts: string[] = [];
  if (auspiciousAspects.length > 0) {
    silverParts.push(...auspiciousAspects.map((a) => `${a.title} — ${a.effect.replace(/\.$/, '').toLowerCase()}`));
  }
  if (infoInsights.length > 0 && silverParts.length < 2) {
    silverParts.push(...infoInsights.map((i) => i.effect.replace(/\.$/, '').toLowerCase()));
  }
  if (silverParts.length > 0) {
    sections.push(`🛡️ ${silverParts.join(', and ')}.`);
  }

  if (sections.length === 0) {
    return insights.slice(0, 3).map((i) => `${i.title}: ${i.effect}`).join(' ');
  }

  return sections.join(' ');
};

const SEVERITY_EMOJI: Record<string, string> = {
  caution: '⚠️',
  auspicious: '✅',
  info: 'ℹ️',
};

export const InsightsNarrative: React.FC<InsightsNarrativeProps> = ({ insights, date, festivals }) => {
  const [serverNarrative, setServerNarrative] = useState<string | null>(null);
  const [yutiRecords, setYutiRecords] = useState<YutiRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    const fetchNarrative = async () => {
      setLoading(true);
      try {
        const url = `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.insights}?date=${date}`;
        const resp = await fetch(url);
        if (resp.ok) {
          const data = await resp.json();
          setServerNarrative(data.narrative);
          setYutiRecords(Array.isArray(data.yuti_records) ? data.yuti_records : []);
        }
      } catch {
        // Silently fall back to local narrative
      } finally {
        setLoading(false);
      }
    };
    fetchNarrative();
  }, [date]);

  const localNarrative = useMemo(() => weaveNarrative(insights), [insights]);

  // Build location-aware festival section from panchanga data
  const festivalSection = useMemo(() => buildFestivalSection(festivals || []), [festivals]);

  const ayurvedaSection = useMemo(() => extractAyurvedaTips(festivals || []), [festivals]);

  // v3 server narratives lead with 🪷 (when there's a festival) or 🧭, and
  // carry the dharma verdict + exposition as cohesive prose. Trust them whole,
  // then append the 🍵 Ayurveda section on ṛtu-begin days (v3 prompt doesn't
  // include seasonal dietary guidance). Legacy narratives already bake the
  // Ayurveda tip into the festival description, so no append there.
  const displayText = useMemo(() => {
    const narrative = serverNarrative || localNarrative;
    if (!narrative) return '';
    const isV3 = narrative.includes('🪷') || narrative.includes('🧭');
    if (isV3) {
      return ayurvedaSection ? `${narrative} ${ayurvedaSection}` : narrative;
    }
    // Legacy path: strip anything before ✨⚡⚠️🛡️, prepend local festival
    const markers = ['✨', '⚡', '⚠️', '🛡️'];
    let firstIdx = narrative.length;
    for (const m of markers) {
      const idx = narrative.indexOf(m);
      if (idx >= 0 && idx < firstIdx) firstIdx = idx;
    }
    const planetary = firstIdx < narrative.length ? narrative.substring(firstIdx) : narrative;
    return [festivalSection, planetary].filter(Boolean).join(' ');
  }, [serverNarrative, localNarrative, festivalSection, ayurvedaSection]);

  if (!displayText) return null;

  return (
    <div className="kg-warm" style={{ minWidth: 280, maxWidth: 420 }}>
      <h3 className="font-fraunces text-lg font-medium mb-1 text-center" style={{ marginTop: 0, color: 'var(--kg-accent)' }}>
        Jyotiṣa Dṛṣṭi
      </h3>
      <p className="text-xs italic mb-3" style={{ color: 'var(--kg-sub)' }}>
        AI-generated insights, reviewed and curated by a human
      </p>
      {loading ? (
        <p className="text-sm italic" style={{ color: 'var(--kg-sub)' }}>Loading...</p>
      ) : (
        <div className="text-sm leading-relaxed space-y-2 text-justify" style={{ color: 'var(--kg-ink)' }}>
          {displayText.split(/(?=🪷|✨|⚡|⚠️|🛡️|🧭|🍵)/).filter(Boolean).map((section, i) => (
            <p key={i} className="m-0 text-justify">{section.trim()}</p>
          ))}
        </div>
      )}

      {/* Additional Details — collapsed by default */}
      {(insights.length > 0 || yutiRecords.length > 0) && (
        <div className="mt-4">
          <button
            onClick={() => setDetailsOpen(!detailsOpen)}
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            <span style={{ display: 'inline-block', transform: detailsOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              ▶
            </span>
            Additional Details
          </button>

          {detailsOpen && (
            <div className="mt-3 space-y-2.5">
              {yutiRecords.map((record, i) => (
                <YutiCard key={`yuti-${i}`} record={record} />
              ))}
              {insights.map((insight, i) => (
                <div key={`detail-${i}`} className="text-sm">
                  <div className="flex items-start gap-2">
                    <span className="flex-shrink-0 mt-0.5">{SEVERITY_EMOJI[insight.severity] || 'ℹ️'}</span>
                    <div>
                      <span className="font-semibold text-foreground">{insight.title}</span>
                      <span className="text-muted-foreground"> — {insight.description}</span>
                      <p className="text-foreground mt-0.5">{insight.effect}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
